import { useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router";
import { ChevronLeft, Building2, History, KeyRound, Layers, Settings2, ShieldCheck, Users } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useDocumentTitle } from "../../app/useDocumentTitle.js";
import { useCollections, useOrg, useOrgKeyReady, useOrgMembers } from "../../data/teams.js";
import { ROLE_LABEL, canManageTeam, describeTeamError, isTeamTwoFactorError } from "../../lib/teams.js";
import type { TeamRole } from "../../api/teams.js";
import { paths } from "../../app/paths.js";
import { Alert, Badge, Button, EmptyState, buttonClass, Page, PageHeader, Skeleton, Tabs } from "../../ui/primitives.js";
import type { SegmentOption } from "../../ui/primitives.js";
import { TeamAuditTab } from "./TeamAuditTab.js";
import { TeamCollectionsTab } from "./TeamCollectionsTab.js";
import { TeamItemsTab } from "./TeamItemsTab.js";
import { TeamMembersTab } from "./TeamMembersTab.js";
import { TeamSettingsTab } from "./TeamSettingsTab.js";

type Tab = "items" | "members" | "collections" | "audit" | "settings";

function RoleBadge(props: { readonly role: TeamRole }): ReactNode {
  return <Badge tone={props.role === "owner" ? "mint" : "neutral"} icon={ShieldCheck}>{ROLE_LABEL[props.role]}</Badge>;
}

export function TeamDetailPage(): ReactNode {
  const params = useParams();
  const id = params["id"] ?? "";
  const org = useOrg(id);
  const detail = org.data;
  const keyReady = useOrgKeyReady(detail);
  const collections = useCollections(id);
  const members = useOrgMembers(id);
  const [tab, setTab] = useState<Tab>("items");
  useDocumentTitle(detail?.name ?? "Team");

  if (org.isError) {
    if (isTeamTwoFactorError(org.error)) {
      return (
        <Page>
          <EmptyState
            icon={ShieldCheck}
            title="2FA erforderlich"
            action={<Link to={`${paths.settings}?setup=totp`} className={buttonClass("primary", "md")}>2FA einrichten</Link>}
          >
            {describeTeamError(org.error)}
          </EmptyState>
        </Page>
      );
    }
    return (
      <Page>
        <EmptyState icon={Building2} title="Team nicht gefunden">
          Es gibt kein Team mit dieser Adresse, oder du bist kein Mitglied.
        </EmptyState>
      </Page>
    );
  }

  if (detail === undefined) {
    return (
      <Page>
        <Skeleton className="h-10 w-64" />
        <Skeleton className="mt-6 h-64" />
      </Page>
    );
  }

  const manage = canManageTeam(detail.role);
  const tabs: ReadonlyArray<SegmentOption<Tab> & { readonly icon: LucideIcon }> = [
    { value: "items", label: "Einträge", icon: KeyRound },
    { value: "members", label: "Mitglieder", icon: Users },
    { value: "collections", label: "Sammlungen", icon: Layers },
    { value: "audit", label: "Protokoll", icon: History },
    { value: "settings", label: "Einstellungen", icon: Settings2 }
  ];

  return (
    <Page>
      <Link to={paths.teams} className="mb-4 inline-flex items-center gap-1 text-[0.8125rem] text-mist transition-colors hover:text-foam">
        <ChevronLeft size={14} aria-hidden="true" />
        Alle Teams
      </Link>
      <PageHeader
          divider={false}
          eyebrow="Team"
          title={detail.name}
          meta={
            <>
              <RoleBadge role={detail.role} />
              {detail.require2fa ? <Badge tone="buoy" icon={ShieldCheck}>2FA erforderlich</Badge> : null}
              <Badge tone="neutral" icon={Users}>{detail.memberCount} {detail.memberCount === 1 ? "Mitglied" : "Mitglieder"}</Badge>
              <Badge tone="neutral" icon={KeyRound}>Schlüssel v{detail.keyVersion}</Badge>
            </>
          }
        />

      <Tabs label="Teambereich" value={tab} options={tabs} onChange={setTab} />

      {detail.rotationPending && manage ? (
        <div className="mt-6">
          <Alert tone="danger">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>Ein Mitglied wurde entfernt. Der Team-Schlüssel muss noch rotiert werden, damit es keinen Zugriff mehr hat.</span>
              <Button size="sm" variant="secondary" onClick={(): void => setTab("settings")}>
                Zu den Einstellungen
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}

      {keyReady.isError ? (
        <div className="mt-6">
          <Alert tone="danger">Der Team-Schlüssel konnte nicht geladen werden. {describeTeamError(keyReady.error)}</Alert>
        </div>
      ) : null}

      <div className="mt-6">
        {tab === "items" ? (
          <TeamItemsTab org={detail} collections={collections.data} keyReady={keyReady.isSuccess} />
        ) : null}
        {tab === "members" ? (
          <TeamMembersTab org={detail} members={members.data} membersPending={members.isPending} membersError={members.isError} />
        ) : null}
        {tab === "collections" ? (
          <TeamCollectionsTab orgId={detail.id} role={detail.role} collections={collections.data} pending={collections.isPending} />
        ) : null}
        {tab === "audit" ? <TeamAuditTab orgId={detail.id} /> : null}
        {tab === "settings" ? <TeamSettingsTab org={detail} members={members.data} /> : null}
      </div>
    </Page>
  );
}
